<template>
  <div class="container-fluid">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <div class="columns is-multiline">
      <div class="column is-12">
        <div class="mt-4">
          <h2>{{ $t('userlist.title') }}</h2>
        </div>

        <!-- Search Input -->
        <div class="mb-4">
          <input
              v-model="query"
              @input="searchUsers"
              class="form-control"
              type="text"
              :placeholder="$t('userlist.search_placeholder')"
          />
        </div>

        <template v-if="users.length">
          <!-- Pagination Buttons -->
          <div class="pagination-controls">
            <button
                class="btn pagination-btn"
                @click="goToPreviousPage()"
                v-if="showPreviousButton"
            >
              <i class="bi bi-arrow-left"></i>
              {{ $t('previous') }}
            </button>
            <button
                class="btn pagination-btn"
                @click="goToNextPage()"
                v-if="showNextButton"
            >
              {{ $t('next') }}
              <i class="bi bi-arrow-right"></i>
            </button>
          </div>
          <table class="table is-fullwidth">
            <thead>
            <tr>
              <th>{{ $t('id') }}</th>
              <th>{{ $t('username') }}</th>
              <th>{{ $t('first_name') }}</th>
              <th>{{ $t('last_name') }}</th>
              <th>{{ $t('email') }}</th>
              <th>{{ $t('position') }}</th>
              <th>{{ $t('location') }}</th>
              <th>{{ $t('date_of_birth') }}</th>
              <th>{{ $t('manager') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="user in users" :key="user.id" @click="goToUserDetail(user.id)" style="cursor: pointer;">
              <td>{{ user.id }}</td>
              <td>{{ user.username }}</td>
              <td>{{ user.first_name }}</td>
              <td>{{ user.last_name }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.position && user.position.name ? user.position.name : $t('noData') }}</td>
              <td>{{ user.location && user.location.name ? user.location.name : $t('noData') }}</td>
              <td>{{ formatDate(user.date_of_birth) }}</td>
              <td>{{
                  user.manager ? user.manager.first_name + ' ' + user.manager.last_name : $t('userlist.no_manager')
                }}
              </td>
              <td>
                <button v-if="hasPermission('users.change_user')" class="btn btn-primary btn-sm"
                        @click="goToUserDetail(user.id)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <button v-if="hasPermission('users.delete_user')" @click.stop="openConfirmDelete(user.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- No users available -->
        <div v-else>
          <p>{{ $t('userlist.no_users') }}</p>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('userlist.confirm_delete') }}</h3>
        <p>{{ $t('userlist.confirm_delete_message') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteUser">{{ $t('confirm') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'UserList',
  data() {
    return {
      users: [],
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      query: '',
      successMessage: '',
      errorMessage: '',
      showConfirm: false,
      userIdToDelete: null,
    };
  },
  mounted() {
    this.getUsers();
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  methods: {
    goToNextPage() {
      this.currentPage += 1;
      this.getUsers();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getUsers();
    },
    goToUserDetail(userId) {
      this.$router.push({name: 'UserDetail', params: {id: userId}});
    },
    async getUsers() {
      this.showNextButton = false;
      this.showPreviousButton = false;

      try {
        const response = await axios.get(`/api/v1/admin/users/?page=${this.currentPage}&search=${this.query}`);
        this.users = response.data.results;

        if (response.data.next) {
          this.showNextButton = true;
        }

        if (response.data.previous) {
          this.showPreviousButton = true;
        }
      } catch (error) {
        this.errorMessage = 'Failed to fetch users';
        setTimeout(() => (this.errorMessage = ''), 2000);
      }
    },
    searchUsers() {
      this.currentPage = 1; // Reset to the first page when searching
      this.getUsers();
    },
    openConfirmDelete(userId) {
      this.userIdToDelete = userId;
      this.showConfirm = true;
    },
    async deleteUser() {
      try {
        await axios.delete(`/api/v1/admin/users/${this.userIdToDelete}/`);
        this.successMessage = 'User deleted successfully';
        this.errorMessage = '';
        this.getUsers(); // Refresh the user list after deletion
        setTimeout(() => (this.successMessage = ''), 2000);
      } catch (error) {
        this.errorMessage = 'Failed to delete user';
        setTimeout(() => (this.errorMessage = ''), 2000);
      }
      this.showConfirm = false; // Close the confirm modal
    },
    cancelDelete() {
      this.showConfirm = false; // Close the confirm modal without deleting
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
  },
};
</script>

<style scoped>

tr:hover {
  background-color: lightslategray;
}
</style>
